import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react'
import { usePopoverDismiss } from './usePopoverDismiss'

// MPD-64 — the shared core of every ⓘ overlay (InfoTip, StatusText):
// - Escape and focus return are usePopoverDismiss (innermost popover only, handled Escapes ignored).
// - A press outside `containerRef` closes it. The listeners exist only while it is open.
// - One overlay at a time: opening one closes the overlay that was open before.
// Positioning is separate (useFixedPanel): StatusText keeps its own slot-wide overlay.

let openOverlay: (() => void) | null = null

export function useOverlayPopover({
  open,
  close,
  triggerRef,
  containerRef,
}: {
  open: boolean
  close: () => void
  triggerRef: RefObject<HTMLElement | null>
  containerRef: RefObject<HTMLElement | null>
}) {
  const closeRef = useRef(close)
  closeRef.current = close
  const dismiss = usePopoverDismiss({ open, close, triggerRef, containerRef })

  useEffect(() => {
    if (!open) return
    const mine = () => closeRef.current()
    if (openOverlay) openOverlay()
    openOverlay = mine
    const onDown = (e: Event) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) closeRef.current()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
      if (openOverlay === mine) openOverlay = null
    }
  }, [open, containerRef])

  return dismiss
}

const MARGIN = 8
const GAP = 4

/** Places a `position: fixed` panel next to its trigger, inside the viewport (MPD-64).
 *
 * Below the trigger, left edges aligned; shifted left when it would pass the right edge,
 * flipped above when there is no room below. Fixed positioning keeps it out of the layout
 * (nothing moves) and out of reach of ancestors that clip their overflow. An ancestor with a
 * transform (the dashboard grid) moves the fixed origin; the second pass measures where the
 * panel landed and corrects by the difference. Follows scroll and resize while open, and
 * closes once the trigger has left the viewport.
 */
export function useFixedPanel({
  open,
  close,
  triggerRef,
  panelRef,
}: {
  open: boolean
  close: () => void
  triggerRef: RefObject<HTMLElement | null>
  panelRef: RefObject<HTMLElement | null>
}) {
  const closeRef = useRef(close)
  closeRef.current = close

  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const trigger = triggerRef.current
      const panel = panelRef.current
      if (!trigger || !panel) return
      const vw = window.innerWidth
      const vh = window.innerHeight
      const t = trigger.getBoundingClientRect()
      if (t.bottom < 0 || t.top > vh || t.right < 0 || t.left > vw) {
        closeRef.current()
        return
      }
      panel.style.maxWidth = `${Math.max(0, Math.min(320, vw - 2 * MARGIN))}px`
      panel.style.left = '0px'
      panel.style.top = '0px'
      const origin = panel.getBoundingClientRect()
      const width = origin.width
      const height = origin.height
      const left = Math.max(MARGIN, Math.min(t.left, vw - MARGIN - width))
      const below = t.bottom + GAP
      const above = t.top - GAP - height
      const flip = below + height > vh - MARGIN && above >= MARGIN
      const top = flip ? above : below
      panel.dataset.placement = flip ? 'above' : 'below'
      // Origin is (0, 0) in the viewport unless a transformed ancestor moved it.
      panel.style.left = `${left - origin.left}px`
      panel.style.top = `${top - origin.top}px`
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, triggerRef, panelRef])
}
